import { useMemo } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';

import { useFeature } from 'src/app/FeatureToggle';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DateControls } from 'src/common/date/DateControls';
import { SelectedDateDisplay } from 'src/common/date/SelectedDateDisplay';
import { convertLocalDaysToDaysOfWeek } from 'src/features/trip/routeTemplates/forms/daysOfWeekMaps';
import { RouteTemplateFormModal } from 'src/features/trip/routeTemplates/modals/RouteTemplateFormModal';
import { routeTemplateActions } from 'src/features/trip/routeTemplates/routeTemplates.slice';

import { useDispatchRepository } from '../../common/useDispatchRepository';
import { dispatcherSlice } from '../../dispatcher.slice';
import { AssignedTripSearch } from '../AssignedTripSearch';
import { DispatchCalendarFilters } from './DispatchCalendarFilters/DispatchCalendarFilters';
import { DispatchCalendarHeaderMenu } from './DispatchCalendarHeaderMenu/DispatchCalendarHeaderMenu';
import { MileageBadge } from './DispatchCalendarHeaderMenu/MileageBadge';
import { DispatchCalendarTimeColumn } from './DispatchCalendarTimeColumn/DispatchCalendarTimeColumn';
import { EstimatedRevenueBadge } from './EstimatedRevenueBadge';
import TripStatusAggregation from './TripStatusAggregation';

export function DispatchCalendarHeader() {
  const dispatch = useAppDispatch();
  const selectedDate = useAppSelector((state) => state.dispatcher.selectedDate);

  const { isFetching: dispatchFetching, data: dispatchData } =
    useDispatchRepository();

  const isMenuActionInProgress = useAppSelector(
    (state) => state.dispatcher.isMenuActionInProgress
  );

  const totalRevenue = useMemo(
    () =>
      dispatchData?.assignments.reduce(
        (acc, assignment) => acc + (assignment.estimated_revenue ?? 0),
        0
      ) ?? 0,
    [dispatchData?.assignments]
  );

  const totalUnloadedMiles = useMemo(
    () =>
      dispatchData?.assignments.reduce(
        (acc, assignment) => acc + (assignment.unloaded_miles ?? 0),
        0
      ) ?? 0,
    [dispatchData?.assignments]
  );

  const totalLoadedMiles = useMemo(
    () =>
      dispatchData?.assignments.reduce(
        (acc, assignment) => acc + (assignment.loaded_miles ?? 0),
        0
      ) ?? 0,
    [dispatchData?.assignments]
  );

  const { isEnabled: isItineraryEnabled, isLoading: isItineraryLoading } =
    useFeature('itinerary');

  const navigate = useNavigate();

  return (
    <>
      <div className="w-full pb-3 pt-3 pr-3">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row gap-2">
            <div className="flex items-center">
              <DispatchCalendarTimeColumn />
              <SelectedDateDisplay date={DateTime.fromISO(selectedDate)} />
              {dispatchFetching || isMenuActionInProgress ? (
                <div className="pl-2">
                  <LoadingIndicator />
                </div>
              ) : (
                <div
                  className="w-10 pl-2"
                  data-testid="loading-indicator-placeholder"
                />
              )}
            </div>
            <div className="flex justify-center">
              <DateControls
                current={DateTime.fromISO(selectedDate)}
                onPrev={() => {
                  dispatch(
                    dispatcherSlice.actions.setSelectedDateToPreviousDay()
                  );

                  const previousDayISO = DateTime.fromISO(selectedDate)
                    .minus({ day: 1 })
                    .toISODate();
                  dispatch(
                    routeTemplateActions.setFilterDaysOfWeek(
                      convertLocalDaysToDaysOfWeek([
                        DateTime.fromISO(previousDayISO).toFormat('cccc'),
                      ])
                    )
                  );
                  navigate(`/dispatch/${previousDayISO}`);
                }}
                onNext={() => {
                  dispatch(dispatcherSlice.actions.setSelectedDateToNextDay());
                  const nextDayISO = DateTime.fromISO(selectedDate)
                    .plus({ day: 1 })
                    .toISODate();

                  dispatch(
                    routeTemplateActions.setFilterDaysOfWeek(
                      convertLocalDaysToDaysOfWeek([
                        DateTime.fromISO(nextDayISO).toFormat('cccc'),
                      ])
                    )
                  );

                  navigate(`/dispatch/${nextDayISO}`);
                }}
                onToday={() => {
                  dispatch(dispatcherSlice.actions.setSelectedDateToToday());
                  const todayISO = DateTime.local().toISODate();
                  dispatch(
                    routeTemplateActions.setFilterDaysOfWeek(
                      convertLocalDaysToDaysOfWeek([
                        DateTime.fromISO(todayISO).toFormat('cccc'),
                      ])
                    )
                  );

                  navigate(`/dispatch/${todayISO}`);
                }}
                onDatePicked={(date) => {
                  dispatch(
                    dispatcherSlice.actions.setSelectedDate(date?.toISODate())
                  );

                  if (date) {
                    dispatch(
                      routeTemplateActions.setFilterDaysOfWeek(
                        convertLocalDaysToDaysOfWeek([
                          DateTime.fromISO(date.toISODate()).toFormat('cccc'),
                        ])
                      )
                    );
                  }
                  navigate(`/dispatch/${date?.toISODate()}`);
                }}
                prevTooltip="Previous Day"
                nextTooltip="Next Day"
              />
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="max-w-[350px] flex-grow">
                <AssignedTripSearch />
              </div>
              <DispatchCalendarFilters />
            </div>
          </div>
          <div className="flex items-center justify-end gap-x-1">
            <div className="mr-2 flex items-center">
              {!isItineraryLoading && isItineraryEnabled && (
                <div className="flex flex-col gap-1">
                  <SimpleTooltip
                    trigger={
                      <div>
                        <MileageBadge
                          miles={totalUnloadedMiles}
                          type="unloaded"
                        />
                      </div>
                    }
                    content="Total unloaded miles for the day's assignments"
                  />
                  <SimpleTooltip
                    trigger={
                      <div>
                        <MileageBadge miles={totalLoadedMiles} type="loaded" />
                      </div>
                    }
                    content="Total loaded miles for the day's assignments"
                  />
                </div>
              )}
              {dispatchData?.show_pricing ? (
                <SimpleTooltip
                  trigger={
                    <div>
                      <EstimatedRevenueBadge revenue={totalRevenue} />
                    </div>
                  }
                  content="Total estimated revenue for the day's assignments"
                />
              ) : null}
            </div>
            <TripStatusAggregation />
            <DispatchCalendarHeaderMenu />
          </div>
        </div>
      </div>
      <RouteTemplateFormModal />
    </>
  );
}
